import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// Лицевая сторона карты. Данные приходят из GET /cards: реквизиты наружу
// провайдер не отдаёт, поэтому номер здесь всегда маскированный — рисуем
// last4, и это не временная заглушка, а потолок того, что вообще доступно.
//
// Карта цельная: никакого разреза, просто градиент. Раз в семь секунд по ней
// бьёт разряд — сначала сверху вниз прочерчивается ствол, потом он рассыпается
// деревом ветвей, карта вспыхивает, свет расходится к кромкам, и через полторы
// секунды не остаётся ничего.
//
// viewBox повторяет пропорции карты 1.586:1, поэтому размытие ореола не
// растягивается по горизонтали. Координаты — в этой же системе.
const TRUNK = "104,0 98,7 103,14 92,23 97,31 84,41 89,49 74,60 79,69 62,80 66,88 52,100";

// Ветви растут из узлов ствола и никогда не сходятся обратно — так ведёт себя
// настоящий разряд. Каждое следующее поколение короче, тоньше и запаздывает:
// ветка не может вспыхнуть раньше, чем разряд дошёл до её основания.
const BRANCHES = [
  // первое поколение — крупные, уходят далеко от ствола
  { points: "103,14 118,19 113,27 126,33 122,41", gen: 1 },
  { points: "97,31 82,35 86,43 72,49", gen: 1 },
  { points: "89,49 105,54 101,62 114,68", gen: 1 },
  { points: "79,69 64,74 68,82 55,88", gen: 1 },
  { points: "66,88 80,92 77,98", gen: 1 },
  // второе — вдвое короче, но ещё заметные
  { points: "118,19 130,15 138,20", gen: 2 },
  { points: "126,33 137,38 134,45", gen: 2 },
  { points: "82,35 70,31 62,35", gen: 2 },
  { points: "72,49 60,53 63,60", gen: 2 },
  { points: "105,54 110,46 120,47", gen: 2 },
  { points: "114,68 126,72 123,79", gen: 2 },
  { points: "64,74 52,70 45,75", gen: 2 },
  { points: "68,82 57,86 60,93", gen: 2 },
  // третье — нити на самом краю дерева
  { points: "138,20 146,17", gen: 3 },
  { points: "134,45 143,49", gen: 3 },
  { points: "62,35 53,32", gen: 3 },
  { points: "63,60 54,64", gen: 3 },
  { points: "120,47 128,42", gen: 3 },
  { points: "123,79 132,83", gen: 3 },
  { points: "45,75 36,72", gen: 3 },
  { points: "60,93 51,96", gen: 3 },
];

const GEN = {
  1: { width: 0.85, className: "lp-bolt-g1" },
  2: { width: 0.5, className: "lp-bolt-g2" },
  3: { width: 0.28, className: "lp-bolt-g3" },
};

const SchemeMark = ({ scheme }) => {
  if (scheme === "mastercard") {
    return (
      <div className="flex items-center" aria-label="Mastercard">
        <span className="h-7 w-7 rounded-full bg-[#eb001b]" />
        <span className="-ml-3 h-7 w-7 rounded-full bg-[#f79e1b] mix-blend-hard-light" />
      </div>
    );
  }
  return (
    <span
      className="text-lg font-black italic tracking-tighter text-white drop-shadow-[0_1px_6px_rgba(255,255,255,0.35)]"
      aria-label="Visa"
    >
      VISA
    </span>
  );
};

const Contactless = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white/55" aria-hidden="true">
    {[6, 10.5, 15].map((x, i) => (
      <path
        key={x}
        d={`M${x} ${7 - i} a ${8 + i * 3} ${8 + i * 3} 0 0 1 0 ${10 + i * 2}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    ))}
  </svg>
);

const Chip = () => (
  <div className="relative h-7 w-9 overflow-hidden rounded-[5px] bg-gradient-to-br from-[#fdf1c4] via-[#c9a441] to-[#7a5c17] shadow-[inset_0_1px_1px_rgba(255,255,255,0.75),0_1px_2px_rgba(0,0,0,0.35)]">
    <div className="absolute inset-x-1 top-1/2 h-px -translate-y-1/2 bg-black/25" />
    <div className="absolute inset-y-1 left-1/2 w-px -translate-x-1/2 bg-black/25" />
    <div className="absolute inset-x-1.5 top-[30%] h-px bg-black/15" />
    <div className="absolute inset-x-1.5 top-[70%] h-px bg-black/15" />
    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/35 to-transparent" />
  </div>
);

const Bolt = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M13.5 2 4 13.4h6.2L9.4 22 20 10.2h-6.6L13.5 2Z" fill="currentColor" />
  </svg>
);

const CardFace = ({
  scheme = "visa",
  last4,
  expMonth,
  expYear,
  holder,
  status,
  statusLabel,
  // Баланс живёт НА карте, а не в панели под ней: карта и есть счёт, разносить
  // их по двум блокам — выдумывать сущность. null означает «показывать нечего»
  // (выпускаемая карта, витрина), и тогда строки просто нет.
  balanceMinor,
  // Реквизиты. Есть — карта развёрнута изнанкой, нет — лежит лицом вверх.
  // Показывать их отдельной панелью под картой значило бы рассказывать про
  // карту рядом с картой, вместо того чтобы показать саму карту.
  secure,
  onCopy,
  className,
}) => {
  const { t } = useTranslation();
  const exp =
    expMonth && expYear
      ? `${String(expMonth).padStart(2, "0")}/${String(expYear).slice(-2)}`
      : "••/••";
  const frozen = status === "frozen";
  const hasBalance = balanceMinor !== null && balanceMinor !== undefined;
  const flipped = Boolean(secure);
  const securePan = String(secure?.pan || "").replace(/(.{4})/g, "$1 ").trim();
  const secureExp = secure
    ? `${String(secure.expMonth).padStart(2, "0")}/${String(secure.expYear).slice(-2)}`
    : "";

  return (
    <div
      className={cn("lp-flip select-none", className)}
      data-flipped={flipped}
      data-testid="card-face"
    >
     <div className="lp-flip-inner">
      <div
        className={cn(
          "lp-flip-face lp-flip-front overflow-hidden rounded-[22px]",
          "shadow-[0_22px_45px_-18px_rgba(37,99,235,0.75),0_6px_18px_-8px_rgba(0,0,0,0.6)]",
          frozen && "saturate-[0.25] brightness-[0.85]"
        )}
      >
      {/* основа: почти чёрный пластик с фирменным синим */}
      <div className="absolute inset-0 bg-[linear-gradient(135deg,#0a1120_0%,#12203c_44%,#070c18_100%)]" />
      {/* Аврора: синий уходит в фиолетовый и бирюзу. Пятна медленно плывут, у
          каждого своя длительность — 17, 23 и 29 секунд. Кратными их делать
          нельзя: совпали бы фазы, и через полминуты картинка начала бы
          заметно повторяться. */}
      <div className="absolute -left-1/4 -top-1/3 h-[130%] w-[85%] rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.8)_0%,rgba(59,130,246,0)_65%)] blur-[2px] lp-aurora-a" />
      <div className="absolute -right-1/4 top-1/4 h-[120%] w-[70%] rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.6)_0%,rgba(139,92,246,0)_65%)] lp-aurora-b" />
      <div className="absolute -bottom-1/3 left-1/4 h-[90%] w-[70%] rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.4)_0%,rgba(34,211,238,0)_70%)] lp-aurora-c" />
      {/* постоянный глянец: широкая мягкая полоса сверху слева и блик в углу */}
      <div className="absolute inset-0 bg-[linear-gradient(128deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.05)_26%,rgba(255,255,255,0)_46%)]" />
      <div className="absolute -left-[15%] -top-[35%] h-[95%] w-[75%] rounded-full bg-[radial-gradient(ellipse,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0)_70%)] lp-aurora-b" />
      {/* зерно, чтобы градиент не выглядел пластмассовым */}
      <div className="absolute inset-0 opacity-[0.13] mix-blend-overlay lp-card-grain" />

      <div className="absolute inset-0 flex flex-col justify-between p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-1.5">
            <Bolt className="h-4 w-4 text-white drop-shadow-[0_0_8px_rgba(96,165,250,0.9)]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/90">
              LightPay
            </span>
          </div>
          <Contactless />
        </div>

        <div className="space-y-3">
          {hasBalance && (
            <div className="flex items-baseline gap-1.5 leading-none">
              <span className="text-[11px] font-semibold text-white/55">$</span>
              <span className="text-[23px] font-bold tabular-nums tracking-[-0.03em] text-white drop-shadow-[0_1px_10px_rgba(255,255,255,0.25)]">
                {(balanceMinor / 100).toFixed(2)}
              </span>
            </div>
          )}
          <Chip />
          <div className="font-mono text-[19px] font-medium tabular-nums tracking-[0.14em] text-white drop-shadow-[0_1px_10px_rgba(255,255,255,0.3)]">
            ••••&nbsp;••••&nbsp;••••&nbsp;{last4 || "••••"}
          </div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[8px] uppercase tracking-[0.18em] text-white/40">Card holder</div>
            <div className="truncate text-[11px] uppercase tracking-[0.12em] text-white/80">
              {holder || " "}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[8px] uppercase tracking-[0.18em] text-white/40">Valid thru</div>
            <div className="font-mono text-[11px] tabular-nums text-white/85">{exp}</div>
          </div>
          <div className="shrink-0">
            <SchemeMark scheme={scheme} />
          </div>
        </div>
      </div>

      {/* Голографическая плёнка. Лежит ПОВЕРХ вёрстки, а не под ней: на
          настоящей карте перелив идёт по всей поверхности, включая номер, и
          если пустить его под текстом, эффекта не будет видно вовсе. Два слоя
          движутся навстречу с разной скоростью — от одного получается ровный
          проезд блика, а не перелив. */}
      <div className="pointer-events-none absolute inset-0 lp-holo" />
      <div className="pointer-events-none absolute inset-0 lp-holo lp-holo--back" />

      {/* Вспышка освещает карту целиком, вместе с номером и логотипом. Без неё
          удар выглядит нарисованным поверх карты, а не случившимся с ней. */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,rgba(224,242,254,0.85)_0%,rgba(147,197,253,0.35)_38%,rgba(147,197,253,0)_72%)] lp-card-flash" />

      {/* Свет, выброшенный разрядом, уходит от ствола к кромкам. Разреза нет,
          поэтому обрезка здесь нужна только чтобы левая волна не выехала
          вправо, а правая влево: иначе вместо расхода из ствола получится
          блик, проезжающий карту насквозь. */}
      <div className="pointer-events-none absolute inset-0 lp-side-left">
        <div className="absolute inset-0 lp-card-wave lp-card-wave--left" />
        <div className="absolute inset-0 lp-card-wave lp-card-wave--left lp-card-wave--echo" />
      </div>
      <div className="pointer-events-none absolute inset-0 lp-side-right">
        <div className="absolute inset-0 lp-card-wave lp-card-wave--right" />
        <div className="absolute inset-0 lp-card-wave lp-card-wave--right lp-card-wave--echo" />
      </div>

      <svg
        viewBox="0 0 158.6 100"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full mix-blend-screen"
        aria-hidden="true"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Между ударами не видно ничего: ни ствола, ни следа. Обычная карта. */}

        {/* Ореолы: широкие и размытые, дают свечение вокруг разряда */}
        <polyline
          points={TRUNK}
          pathLength="100"
          stroke="rgba(96,165,250,0.7)"
          strokeWidth="4.2"
          className="lp-bolt-main"
          style={{ filter: "blur(3px)" }}
        />
        {BRANCHES.filter((b) => b.gen === 1).map(({ points }) => (
          <polyline
            key={`halo-${points}`}
            points={points}
            pathLength="100"
            stroke="rgba(96,165,250,0.5)"
            strokeWidth="2.4"
            className="lp-bolt-g1"
            style={{ filter: "blur(2px)" }}
          />
        ))}

        {/* Ветви: чем дальше поколение, тем тоньше и позже */}
        {BRANCHES.map(({ points, gen }) => (
          <polyline
            key={points}
            points={points}
            pathLength="100"
            stroke="rgba(219,234,254,0.92)"
            strokeWidth={GEN[gen].width}
            className={GEN[gen].className}
          />
        ))}

        {/* Ствол и раскалённая нить внутри него */}
        <polyline
          points={TRUNK}
          pathLength="100"
          stroke="rgba(191,219,254,0.95)"
          strokeWidth="1.4"
          className="lp-bolt-main"
        />
        <polyline
          points={TRUNK}
          pathLength="100"
          stroke="rgba(255,255,255,0.98)"
          strokeWidth="0.5"
          className="lp-bolt-core"
        />
      </svg>

      <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/15" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/55 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-sky-300/25 to-transparent" />

      {frozen && (
        <div className="pointer-events-none absolute inset-0 bg-sky-100/10 backdrop-blur-[1px]" />
      )}

      {statusLabel && (
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/55 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/95 shadow-lg ring-1 ring-inset ring-white/20 backdrop-blur-sm">
          {statusLabel}
        </span>
      )}
      </div>

      {/* Изнанка. Тот же пластик, что и лицо, — иначе разворот читается не как
          поворот карты, а как подмена картинки. Аврора здесь глуше: тут не на
          что любоваться, тут надо прочитать шестнадцать цифр. */}
      <div
        className={cn(
          "lp-flip-face lp-flip-back overflow-hidden rounded-[22px]",
          "shadow-[0_22px_45px_-18px_rgba(37,99,235,0.75),0_6px_18px_-8px_rgba(0,0,0,0.6)]"
        )}
      >
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#0a1120_0%,#12203c_44%,#070c18_100%)]" />
        <div className="absolute -left-1/4 -top-1/3 h-[120%] w-[80%] rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.35)_0%,rgba(59,130,246,0)_66%)]" />
        <div className="absolute -right-1/4 bottom-0 h-[110%] w-[65%] rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.25)_0%,rgba(139,92,246,0)_66%)]" />
        <div className="absolute inset-0 opacity-[0.13] mix-blend-overlay lp-card-grain" />

        <div className="absolute inset-0 flex flex-col">
          <div className="lp-stripe mt-[9%] h-[15%] w-full" />

          <div className="flex flex-1 flex-col justify-center gap-2 px-[5.5%] py-[4%]">
            <button
              type="button"
              onClick={() => onCopy?.(secure?.pan, t("cards.secure_pan"))}
              className="flex w-full items-center justify-between gap-2 rounded-lg bg-white/[0.07] px-2.5 py-1.5 text-left ring-1 ring-inset ring-white/10 active:bg-white/[0.13]"
              data-testid="secure-pan"
            >
              <span className="min-w-0">
                <span className="block text-[7px] uppercase tracking-[0.17em] text-white/45">
                  {t("cards.secure_pan")}
                </span>
                <span className="block font-mono text-[15px] font-medium tabular-nums tracking-[0.08em] text-white">
                  {securePan || "•••• •••• •••• ••••"}
                </span>
              </span>
              <Copy className="h-3.5 w-3.5 shrink-0 text-white/45" />
            </button>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onCopy?.(secureExp, t("cards.secure_exp"))}
                className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.07] px-2.5 py-1.5 text-left ring-1 ring-inset ring-white/10 active:bg-white/[0.13]"
                data-testid="secure-exp"
              >
                <span>
                  <span className="block text-[7px] uppercase tracking-[0.17em] text-white/45">
                    {t("cards.secure_exp")}
                  </span>
                  <span className="block font-mono text-[13px] tabular-nums text-white">{secureExp || "••/••"}</span>
                </span>
                <Copy className="h-3 w-3 shrink-0 text-white/45" />
              </button>

              <button
                type="button"
                onClick={() => onCopy?.(secure?.cvv, "CVV")}
                className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.07] px-2.5 py-1.5 text-left ring-1 ring-inset ring-white/10 active:bg-white/[0.13]"
                data-testid="secure-cvv"
              >
                <span>
                  <span className="block text-[7px] uppercase tracking-[0.17em] text-white/45">CVV</span>
                  <span className="block font-mono text-[13px] tabular-nums text-white">{secure?.cvv || "•••"}</span>
                </span>
                <Copy className="h-3 w-3 shrink-0 text-white/45" />
              </button>
            </div>

            <p className="text-center text-[7.5px] tracking-[0.06em] text-white/40">
              {t("cards.secure_copy_hint")}
            </p>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/15" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/55 to-transparent" />
      </div>
     </div>
    </div>
  );
};

export default CardFace;
